import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Plus, Search, Filter, MapPin, Locate, Sliders, GitCompareArrows, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card } from "../components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "../components/ui/alert-dialog";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { StatusBadge } from "../components/Layout";
import { Badge } from "../components/ui/badge";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { Checkbox } from "../components/ui/checkbox";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import PropertyHoverPreview from "../components/PropertyHoverPreview";

const empty = {
  title: "", address: "", city: "", province: "", country: "Italy",
  property_type: "residential", status: "off_market",
  price: 0, estimated_value: 0, size_sqm: 0, rooms: 0, bathrooms: 0, year_built: 0,
  description: "", tags: [], hero_image: "",
  residual_debt: 0, potential_margin: 0, dossier_status: "draft", doc_completeness: 0, position_score: 50,
  legal_complexity: 30, estimated_days: 90,
  deal_start_date: "", deal_deadline: "",
  latitude: null, longitude: null,
  unlock_price: "",
};

export default function Properties() {
  const [params, setParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [q, setQ] = useState(params.get("q") || "");
  const [status, setStatus] = useState("all");
  const [propType, setPropType] = useState("all");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);
  const [selected, setSelected] = useState([]);
  const [radar, setRadar] = useState({ min_score: "", min_position: "", max_legal: "", min_documentation: "", min_margin_pct: "", min_interest: "" });
  const navigate = useNavigate();
  const { user } = useAuth();
  const isClient = user?.role === "client";
  const canDelete = user && user.role !== "client";
  const [toDelete, setToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const removeProperty = async () => {
    if (!toDelete) return;
    setDeleting(true);
    try {
      await api.delete(`/properties/${toDelete.id}`);
      toast.success(`"${toDelete.title}" eliminato`);
      setItems((prev) => prev.filter((x) => x.id !== toDelete.id));
      setToDelete(null);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setDeleting(false);
    }
  };

  const load = async () => {
    const query = {};
    if (q) query.q = q;
    if (status !== "all") query.status = status;
    if (propType !== "all") query.property_type = propType;
    for (const k of Object.keys(radar)) if (radar[k] !== "") query[k] = radar[k];
    const { data } = await api.get("/properties", { params: query });
    setItems(data);
  };
  useEffect(() => { load(); }, [status, propType, radar]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); /* eslint-disable-next-line */ }, [q]);

  const toggleSelect = (id) => {
    setSelected((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id].slice(-5));
  };
  const goCompare = () => {
    if (selected.length < 2) return;
    navigate(`/compare?ids=${selected.join(",")}`);
  };

  const submit = async () => {
    setBusy(true);
    try {
      await api.post("/properties", { ...form, price: Number(form.price), estimated_value: Number(form.estimated_value), size_sqm: Number(form.size_sqm), rooms: Number(form.rooms), bathrooms: Number(form.bathrooms), year_built: Number(form.year_built), residual_debt: Number(form.residual_debt), potential_margin: Number(form.potential_margin), doc_completeness: Number(form.doc_completeness), position_score: Number(form.position_score), legal_complexity: Number(form.legal_complexity), estimated_days: Number(form.estimated_days), latitude: form.latitude === "" || form.latitude === null ? null : Number(form.latitude), longitude: form.longitude === "" || form.longitude === null ? null : Number(form.longitude), deal_start_date: form.deal_start_date ? new Date(form.deal_start_date).toISOString() : null, deal_deadline: form.deal_deadline ? new Date(form.deal_deadline).toISOString() : null, unlock_price: form.unlock_price === "" || form.unlock_price === null ? null : Number(form.unlock_price) });
      toast.success("Immobile creato");
      setOpen(false); setForm(empty); load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const geocode = async () => {
    if (!form.address && !form.city) return toast.error("Inserisci almeno indirizzo o città");
    try {
      const { data } = await api.post("/geocode", { address: form.address, city: form.city, country: form.country || "Italy" });
      setForm((f) => ({ ...f, latitude: data.latitude, longitude: data.longitude }));
      toast.success(`Coordinate trovate: ${data.latitude.toFixed(4)}, ${data.longitude.toFixed(4)}`);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-6" data-testid="properties-page">
      <header className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl md:text-4xl font-semibold tracking-tight">{isClient ? "Opportunità" : "Immobili"}</h1>
          <p className="text-muted-foreground text-sm mt-1">{isClient ? "Archivio delle opportunità immobiliari off-market a te dedicate." : "Archivio delle opportunità immobiliari."}</p>
        </div>
        {!isClient && (
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="gap-2" data-testid="new-property-button"><Plus className="w-4 h-4" /> Nuovo immobile</Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader><DialogTitle>Nuovo immobile</DialogTitle></DialogHeader>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 py-2">
              <Field label="Titolo *"><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="prop-title-input" /></Field>
              <Field label="Indirizzo *"><Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} data-testid="prop-address-input" /></Field>
              <Field label="Città"><Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} /></Field>
              <Field label="Provincia"><Input value={form.province} onChange={(e) => setForm({ ...form, province: e.target.value })} /></Field>
              <Field label="Tipologia">
                <Select value={form.property_type} onValueChange={(v) => setForm({ ...form, property_type: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="residential">Residenziale</SelectItem>
                    <SelectItem value="commercial">Commerciale</SelectItem>
                    <SelectItem value="land">Terreno</SelectItem>
                    <SelectItem value="industrial">Industriale</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Stato">
                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                  <SelectTrigger data-testid="prop-status-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="off_market">Off market</SelectItem>
                    <SelectItem value="pre_market">Pre-market</SelectItem>
                    <SelectItem value="on_market">On market</SelectItem>
                    <SelectItem value="in_negotiation">In trattativa</SelectItem>
                    <SelectItem value="sold">Venduto</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Prezzo (€)"><Input type="number" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} /></Field>
              <Field label="Valore stimato (€)"><Input type="number" value={form.estimated_value} onChange={(e) => setForm({ ...form, estimated_value: e.target.value })} /></Field>
              <Field label="Superficie (mq)"><Input type="number" value={form.size_sqm} onChange={(e) => setForm({ ...form, size_sqm: e.target.value })} /></Field>
              <Field label="Locali"><Input type="number" value={form.rooms} onChange={(e) => setForm({ ...form, rooms: e.target.value })} /></Field>
              <Field label="Bagni"><Input type="number" value={form.bathrooms} onChange={(e) => setForm({ ...form, bathrooms: e.target.value })} /></Field>
              <Field label="Anno costruzione"><Input type="number" value={form.year_built} onChange={(e) => setForm({ ...form, year_built: e.target.value })} /></Field>
              <div className="md:col-span-2">
                <Field label="Descrizione"><Textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></Field>
              </div>
              <div className="md:col-span-2">
                <Field label="URL immagine hero"><Input value={form.hero_image} onChange={(e) => setForm({ ...form, hero_image: e.target.value })} placeholder="https://..." /></Field>
              </div>
              <div className="md:col-span-2 pt-2 border-t border-border/60">
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">Fattori Opportunity Score</Label>
              </div>
              <Field label="Debito residuo (€)"><Input type="number" value={form.residual_debt} onChange={(e) => setForm({ ...form, residual_debt: e.target.value })} data-testid="prop-debt-input" /></Field>
              <Field label="Margine potenziale (€)"><Input type="number" value={form.potential_margin} onChange={(e) => setForm({ ...form, potential_margin: e.target.value })} data-testid="prop-margin-input" /></Field>
              <Field label="Stato pratica">
                <Select value={form.dossier_status} onValueChange={(v) => setForm({ ...form, dossier_status: v })}>
                  <SelectTrigger data-testid="prop-dossier-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="draft">Bozza</SelectItem>
                    <SelectItem value="in_review">In revisione</SelectItem>
                    <SelectItem value="verified">Verificata</SelectItem>
                    <SelectItem value="ready">Pronta</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Completezza documenti (0-100)"><Input type="number" min="0" max="100" value={form.doc_completeness} onChange={(e) => setForm({ ...form, doc_completeness: e.target.value })} /></Field>
              <Field label="Punteggio posizione (0-100)"><Input type="number" min="0" max="100" value={form.position_score} onChange={(e) => setForm({ ...form, position_score: e.target.value })} /></Field>
              <Field label="Complessità legale (0-100)"><Input type="number" min="0" max="100" value={form.legal_complexity} onChange={(e) => setForm({ ...form, legal_complexity: e.target.value })} data-testid="prop-legal-input" /></Field>
              <Field label="Tempo stimato operazione (giorni)"><Input type="number" min="0" value={form.estimated_days} onChange={(e) => setForm({ ...form, estimated_days: e.target.value })} data-testid="prop-days-input" /></Field>
              <Field label="Data inizio operazione"><Input type="date" value={form.deal_start_date} onChange={(e) => setForm({ ...form, deal_start_date: e.target.value })} data-testid="prop-start-input" /></Field>
              <Field label="Deadline chiusura"><Input type="date" value={form.deal_deadline} onChange={(e) => setForm({ ...form, deal_deadline: e.target.value })} data-testid="prop-deadline-input" /></Field>
              <Field label="Prezzo sblocco allegati (€) · lascia vuoto per usare il default">
                <Input type="number" min="0" step="0.01" value={form.unlock_price ?? ""} onChange={(e) => setForm({ ...form, unlock_price: e.target.value })} placeholder="69.00" data-testid="prop-unlock-price-input" />
              </Field>
              <div />
              <div className="md:col-span-2 flex items-end gap-2">
                <div className="flex-1">
                  <Field label="Latitudine"><Input type="number" step="any" value={form.latitude ?? ""} onChange={(e) => setForm({ ...form, latitude: e.target.value })} placeholder="es. 45.4642" data-testid="prop-lat-input" /></Field>
                </div>
                <div className="flex-1">
                  <Field label="Longitudine"><Input type="number" step="any" value={form.longitude ?? ""} onChange={(e) => setForm({ ...form, longitude: e.target.value })} placeholder="es. 9.19" data-testid="prop-lng-input" /></Field>
                </div>
                <Button type="button" variant="outline" onClick={geocode} className="gap-2 shrink-0" data-testid="geocode-button">
                  <Locate className="w-4 h-4" /> Geolocalizza
                </Button>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>Annulla</Button>
              <Button onClick={submit} disabled={busy || !form.title || !form.address} data-testid="prop-submit-button">{busy ? "Salvataggio…" : "Crea immobile"}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        )}
      </header>

      {/* Filters */}
      <Card className="border-border/60 p-4">
        <div className="flex flex-col md:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input placeholder="Cerca per titolo, indirizzo, descrizione…" className="pl-9" value={q} onChange={(e) => setQ(e.target.value)} data-testid="properties-search" />
          </div>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="w-full md:w-48" data-testid="filter-status"><Filter className="w-3.5 h-3.5 mr-2" /><SelectValue placeholder="Stato" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutti gli stati</SelectItem>
              <SelectItem value="off_market">Off market</SelectItem>
              <SelectItem value="pre_market">Pre-market</SelectItem>
              <SelectItem value="on_market">On market</SelectItem>
              <SelectItem value="in_negotiation">In trattativa</SelectItem>
              <SelectItem value="sold">Venduto</SelectItem>
            </SelectContent>
          </Select>
          <Select value={propType} onValueChange={setPropType}>
            <SelectTrigger className="w-full md:w-48"><SelectValue placeholder="Tipologia" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutte le tipologie</SelectItem>
              <SelectItem value="residential">Residenziale</SelectItem>
              <SelectItem value="commercial">Commerciale</SelectItem>
              <SelectItem value="land">Terreno</SelectItem>
              <SelectItem value="industrial">Industriale</SelectItem>
            </SelectContent>
          </Select>
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" className="gap-2" data-testid="radar-filters-button">
                <Sliders className="w-4 h-4" /> Radar
                {Object.values(radar).some((v) => v !== "") && <span className="ml-1 w-1.5 h-1.5 rounded-full bg-primary" />}
              </Button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-80">
              <div className="space-y-3">
                <div className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">Ricerca per radar</div>
                <RadarField label="Score minimo" value={radar.min_score} onChange={(v) => setRadar({ ...radar, min_score: v })} testid="rf-min-score" />
                <RadarField label="Posizione ≥" value={radar.min_position} onChange={(v) => setRadar({ ...radar, min_position: v })} testid="rf-min-position" />
                <RadarField label="Complessità legale ≤" value={radar.max_legal} onChange={(v) => setRadar({ ...radar, max_legal: v })} testid="rf-max-legal" hint="Da 0 (nessuna) a 100 (massima)" />
                <RadarField label="Documentazione ≥" value={radar.min_documentation} onChange={(v) => setRadar({ ...radar, min_documentation: v })} testid="rf-min-doc" />
                <RadarField label="Margine minimo %" value={radar.min_margin_pct} onChange={(v) => setRadar({ ...radar, min_margin_pct: v })} testid="rf-min-margin" />
                <RadarField label="Interesse investitori ≥" value={radar.min_interest} onChange={(v) => setRadar({ ...radar, min_interest: v })} testid="rf-min-interest" />
                <Button variant="ghost" size="sm" onClick={() => setRadar({ min_score: "", min_position: "", max_legal: "", min_documentation: "", min_margin_pct: "", min_interest: "" })} className="w-full" data-testid="clear-radar">Azzera filtri</Button>
              </div>
            </PopoverContent>
          </Popover>
        </div>
      </Card>

      {selected.length > 0 && (
        <div className="sticky top-16 z-20 flex items-center justify-between gap-3 p-3 bg-primary text-primary-foreground rounded-md shadow-lg" data-testid="compare-bar">
          <span className="text-sm font-medium">{selected.length} immobili selezionati</span>
          <div className="flex gap-2">
            <Button variant="secondary" size="sm" onClick={() => setSelected([])}>Annulla</Button>
            <Button variant="secondary" size="sm" onClick={goCompare} disabled={selected.length < 2} className="gap-2" data-testid="compare-go">
              <GitCompareArrows className="w-4 h-4" /> Confronta
            </Button>
          </div>
        </div>
      )}

      {/* Table */}
      <Card className="border-border/60 overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-8"></TableHead>
              <TableHead>Immobile</TableHead>
              <TableHead>Tipologia</TableHead>
              <TableHead>Stato</TableHead>
              <TableHead className="text-right">Prezzo</TableHead>
              <TableHead className="text-right">Superficie</TableHead>
              <TableHead>Score</TableHead>
              <TableHead>Tags</TableHead>
              {canDelete && <TableHead className="w-12 text-right">Azioni</TableHead>}
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.length === 0 && (
              <TableRow><TableCell colSpan={canDelete ? 9 : 8} className="text-center text-muted-foreground py-16">Nessun immobile trovato. Crea il primo per iniziare.</TableCell></TableRow>
            )}
            {items.map((p) => (
              <TableRow key={p.id} className={`hover:bg-muted/40 cursor-pointer ${selected.includes(p.id) ? "bg-primary/5" : ""}`} data-testid={`prop-row-${p.id}`}>
                <TableCell className="pr-0" onClick={(e) => e.stopPropagation()}>
                  <Checkbox checked={selected.includes(p.id)} onCheckedChange={() => toggleSelect(p.id)} data-testid={`select-${p.id}`} />
                </TableCell>
                <TableCell>
                  <PropertyHoverPreview propertyId={p.id} propertyTitle={p.title} address={p.address} city={p.city}>
                    <Link to={`/properties/${p.id}`} className="flex items-start gap-3" data-testid={`prop-title-${p.id}`}>
                      <div className="w-12 h-12 rounded-md bg-muted overflow-hidden shrink-0">
                        {p.hero_image ? <img src={p.hero_image.startsWith("http") ? p.hero_image : `${p.hero_image.startsWith("/") ? "" : "/"}${p.hero_image}`} alt="" className="w-full h-full object-cover" /> : null}
                      </div>
                      <div>
                        <div className="font-medium text-foreground">{p.title}</div>
                        <div className="text-xs text-muted-foreground flex items-center gap-1 mt-0.5"><MapPin className="w-3 h-3" />{p.address}{p.city ? `, ${p.city}` : ""}</div>
                      </div>
                    </Link>
                  </PropertyHoverPreview>
                </TableCell>
                <TableCell className="text-sm capitalize">{p.property_type}</TableCell>
                <TableCell><StatusBadge status={p.status} /></TableCell>
                <TableCell className="text-right tabular-nums">€ {Intl.NumberFormat("it-IT").format(p.price || 0)}</TableCell>
                <TableCell className="text-right tabular-nums">{p.size_sqm || 0} mq</TableCell>
                <TableCell>{p._score != null && <ScorePill value={p._score} />}</TableCell>
                <TableCell><div className="flex gap-1 flex-wrap">{(p.tags || []).slice(0, 3).map((t) => <Badge key={t} variant="secondary" className="text-xs">{t}</Badge>)}</div></TableCell>
                {canDelete && (
                  <TableCell className="text-right">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-muted-foreground hover:text-destructive hover:bg-destructive/10"
                      onClick={(e) => { e.preventDefault(); e.stopPropagation(); setToDelete(p); }}
                      data-testid={`delete-prop-${p.id}`}
                      title="Elimina immobile"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      {/* Delete confirmation dialog */}
      <AlertDialog open={!!toDelete} onOpenChange={(open) => !open && setToDelete(null)}>
        <AlertDialogContent data-testid="delete-property-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminare l'immobile?</AlertDialogTitle>
            <AlertDialogDescription>
              Stai per eliminare <strong className="text-foreground">{toDelete?.title}</strong>. Verranno rimossi anche foto, documenti e attività collegate. Questa azione non è reversibile.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-cancel">Annulla</AlertDialogCancel>
            <AlertDialogAction
              onClick={removeProperty}
              disabled={deleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="delete-confirm"
            >
              {deleting ? "Eliminazione…" : "Elimina definitivamente"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function Field({ label, children }) {
  return <div className="space-y-1.5"><Label className="text-xs uppercase tracking-wider text-muted-foreground">{label}</Label>{children}</div>;
}

function RadarField({ label, value, onChange, hint, testid }) {
  return (
    <div>
      <Label className="text-xs">{label}</Label>
      <Input type="number" min="0" max="100" value={value} onChange={(e) => onChange(e.target.value)} placeholder="—" className="h-8 text-sm" data-testid={testid} />
      {hint && <p className="text-[10px] text-muted-foreground mt-0.5">{hint}</p>}
    </div>
  );
}

function ScorePill({ value }) {
  const color = value >= 75 ? "bg-emerald-100 text-emerald-700" : value >= 50 ? "bg-blue-100 text-blue-700" : value >= 25 ? "bg-amber-100 text-amber-700" : "bg-rose-100 text-rose-700";
  return <Badge className={`${color} border-transparent tabular-nums`}>{value}</Badge>;
}
